import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui";

const PERSONAS = [
  { email: "site@bhp.com.au", label: "Site Manager", desc: "BHP Pilbara · Monitor", color: "#F97316" },
  { email: "field@autobrains.ai", label: "Technical Integrator", desc: "Autobrains · Configure", color: "#22D3EE" },
  { email: "admin@hsepacific.com.au", label: "Super Admin", desc: "HSE Pacific · Comply", color: "#3B82F6" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("site@bhp.com.au");
  const [password, setPassword] = useState("pacificone");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await login(username, password);
      nav(`/${user.landing.surface}`, { replace: true });
    } catch {
      setError("Invalid credentials. Use a demo persona below (password: pacificone).");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-screen items-center justify-center p-6">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-2xl border border-white/10 md:grid-cols-2">
        {/* Brand panel */}
        <div className="relative hidden flex-col justify-between bg-ocean-900/60 p-8 md:flex">
          <div className="absolute inset-0 opacity-40">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full border border-aurora-cyan/30 animate-radar"
                style={{ animationDelay: `${i}s` }}
              />
            ))}
          </div>
          <div className="relative flex items-center gap-2">
            <Radar className="text-aurora-cyan" />
            <span className="text-lg font-bold">PacificOne</span>
          </div>
          <div className="relative">
            <div className="section-tag">THE TRUSTED INTELLIGENCE LAYER</div>
            <h1 className="mt-2 text-2xl font-bold leading-tight">
              Manage by Exception.
              <br />
              <span className="text-aurora-cyan">Look. Click. Act.</span>
            </h1>
            <p className="mt-3 text-sm text-slatey">
              Predict · Prevent · Protect — synthesizing telematics, governance and
              operator-behaviour into one role-aware operating view.
            </p>
          </div>
          <div className="relative section-tag">HSE PACIFIC × AUTOBRAINS.AI</div>
        </div>

        {/* Login form */}
        <div className="glass !rounded-none p-8">
          <div className="section-tag">SECURE SSO</div>
          <h2 className="mb-5 mt-1 text-xl font-semibold">Sign in</h2>
          <form onSubmit={submit} className="space-y-3">
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="email"
              className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-aurora-cyan/50"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="password"
              className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-aurora-cyan/50"
            />
            {error && <div className="text-xs text-critical">{error}</div>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "Authenticating…" : "Enter PacificOne"}
            </Button>
          </form>

          <div className="section-tag mt-6">PERSONA BLUEPRINTS</div>
          <div className="mt-2 space-y-2">
            {PERSONAS.map((p) => (
              <button
                key={p.email}
                onClick={() => {
                  setUsername(p.email);
                  setPassword("pacificone");
                }}
                className="flex w-full items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-left transition hover:border-aurora-cyan/40"
              >
                <div>
                  <div className="text-sm font-medium" style={{ color: p.color }}>
                    {p.label}
                  </div>
                  <div className="text-[11px] text-slatey">{p.desc}</div>
                </div>
                <ArrowRight size={15} className="text-slatey" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
